import { z } from "zod";
import { ChampignonsSchema } from "./champignons.zod";
import { EspeceSchema } from "./espece.zod";
import { NuisibleSchema } from "./nuisible.zod";

// Define a CoordSchema for properly typed coordinates
export const CoordSchema = z.union([
  // Original string format (for backward compatibility)
  z.string(),
  // Structured GeoJSON-like format
  z.object({
    type: z.literal("Point"),
    coordinates: z.tuple([z.number(), z.number()]), // [longitude, latitude]
    srid: z.number().default(4326)
  }).transform(obj => JSON.stringify(obj)),
  // Simple {lat, lng} format
  z.object({
    lat: z.number(),
    lng: z.number()
  }).transform(obj => obj)
]);

export const ArbreBaseSchema = z.object({
  diametre: z.number().int().nullable().optional(),
  coord: CoordSchema.nullable().optional(),
  age_estime: z.number().int().positive("L'âge estimé doit être un nombre positif").nullable().optional(),
  code_site: z.string().max(255, "Le code site ne peut pas dépasser 255 caractères").nullable().optional(),
  num_sujet: z.string().max(255, "Le numéro de sujet ne peut pas dépasser 255 caractères").nullable().optional(),
  critere: z.any().nullable().optional(), // JSON field
  implantation: z.string().max(255, "L'implantation ne peut pas dépasser 255 caractères").nullable().optional(),
  implantation_other: z.string().max(255, "L'implantation autre ne peut pas dépasser 255 caractères").nullable().optional(),
  domaine: z.string().max(255, "Le domaine ne peut pas dépasser 255 caractères").nullable().optional(),
  nuisance: z.any().nullable().optional(), // JSON field
  proximite: z.any().nullable().optional(), // JSON field
  taux_freq: z.string().max(255, "Le taux de fréquence ne peut pas dépasser 255 caractères").nullable().optional(),
  type_passage: z.any().nullable().optional(), // JSON field
  accessibilite: z.string().max(255, "L'accessibilité ne peut pas dépasser 255 caractères").nullable().optional(),
  abattage: z.string().max(255, "L'abattage ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux_collet: z.string().nullable().optional(),
  travaux_tronc: z.string().nullable().optional(),
  travaux_houppier: z.string().nullable().optional(),
  date_travaux: z.string().max(255, "La date des travaux ne peut pas dépasser 255 caractères").nullable().optional(),
  date_pro_visite: z.string().max(255, "La date de prochaine visite ne peut pas dépasser 255 caractères").nullable().optional(),
  com_pro_visite: z.string().nullable().optional(),
  caract_pied: z.any().nullable().optional(), // JSON field
  caract_tronc: z.string().max(255, "Les caractéristiques du tronc ne peuvent pas dépasser 255 caractères").nullable().optional(),
  hauteur: z.number().int().positive("La hauteur doit être un nombre positif").nullable().optional(),
  port_arbre: z.string().max(255, "Le port de l'arbre ne peut pas dépasser 255 caractères").nullable().optional(),
  stade_dev: z.string().max(255, "Le stade de développement ne peut pas dépasser 255 caractères").nullable().optional(),
  etat_san_collet: z.any().nullable().optional(), // JSON field
  etat_san_tronc: z.any().nullable().optional(), // JSON field
  etat_san_houppier: z.any().nullable().optional(), // JSON field
  address: z.string().max(255, "L'adresse ne peut pas dépasser 255 caractères").nullable().optional(),
  com_access: z.string().nullable().optional(),
  dict: z.any().nullable().optional(), // JSON field
  risque: z.any().nullable().optional(), // JSON field
  proximite_other: z.string().nullable().optional(),
  accessibilite_other: z.string().nullable().optional(),
  caract_pied_other: z.string().nullable().optional(),
  caract_tronc_multiples: z.number().int().nullable().optional(),
  etat_san_collet_cavite: z.number().int().nullable().optional(),
  etat_san_collet_champignons: z.array(ChampignonsSchema), // JSON field
  etat_san_tronc_cavite: z.number().int().nullable().optional(),
  etat_san_tronc_corps_etranger: z.string().max(255).nullable().optional(),
  etat_san_tronc_champignons: z.array(ChampignonsSchema), // JSON field
  etat_san_tronc_nuisibles: z.array(NuisibleSchema), // JSON field
  etat_san_houppier_champignons: z.array(ChampignonsSchema), // JSON field
  etat_san_houppier_nuisibles: z.array(NuisibleSchema),
  risque_general: z.any().nullable().optional(), // JSON field
  travaux_commentaire: z.string().nullable().optional(),
  critere_other: z.string().nullable().optional(),
  risque_general_other: z.string().nullable().optional(),
  type_passage_other: z.string().nullable().optional(),
  type_intervention: z.string().max(255).nullable().optional(),
  etat_san_general: z.any().nullable().optional(), // JSON field
  user_edited_date_travaux: z.string().nullable().optional(),
  user_edited_date_pro_visite: z.string().nullable().optional(),
  travaux_tronc_other: z.string().nullable().optional(),
  travaux_collet_other: z.string().nullable().optional(),
  travaux_houppier_other: z.string().max(2000).nullable().optional(),
  proximite_with_dict: z.any().nullable().optional(), // JSON field
  ville: z.string().max(155).nullable().optional(),
  pays: z.string().max(255).nullable().optional(),
  travaux_tronc_protection: z.string().max(100).nullable().optional(),
  img1: z.string().max(255).nullable().optional(),
  img2: z.string().max(255).nullable().optional(),
  img3: z.string().max(255).nullable().optional(),
  etat_san_tronc_nuisibles_autres: z.string().max(150).nullable().optional(),
  etat_san_houppier_nuisibles_autres: z.string().max(150).nullable().optional(),
  travaux_collet_multiple: z.any().nullable().optional(), // JSON field
  travaux_tronc_multiple: z.any().nullable().optional(), // JSON field
  travaux_houppier_multiple: z.any().nullable().optional(), // JSON field
  status_travaux: z.boolean().default(true).optional(),
  /** Visite réalisée (séparée de status_travaux). */
  status_visite: z.boolean().nullable().optional(),
  etat_san_collet_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_tronc_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_houppier_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_collet_other: z.string().max(255).nullable().optional(),
  etat_san_tronc_other: z.string().nullable().optional(),
  etat_san_houppier_other: z.string().nullable().optional(),
  etat_san_general_other: z.string().max(200).nullable().optional(),
  espece_id: z.number().int().positive("L'ID de l'espèce doit être un nombre positif"),
  espece: EspeceSchema,
  beva: z.number().nullable().optional(),
  is_alert: z.boolean().nullable().optional(),
  imgUrl: z.object({
    img1: z.string().max(255).nullable().optional(),
    img2: z.string().max(255).nullable().optional(),
    img3: z.string().max(255).nullable().optional(),
  })
});

export const ArbreSchema = ArbreBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const ArbreCreateSchema = ArbreBaseSchema.pick({
  diametre: true,
  coord: true,
  code_site: true,
  num_sujet: true,
  hauteur: true,
  espece_id: true,
  age_estime: true,
});

/**
 * Liste d'identifiants (champignons, nuisibles) stockée en JSON. Des fiches anciennes contiennent des identifiants
 * en texte (« 12 ») : on les accepte et on les convertit en nombres, au lieu de refuser toute la mise à jour.
 */
const IdListSchema = z.array(
  z.union([z.number().int(), z.string().regex(/^\d+$/, 'Identifiant invalide').transform(Number)])
);

export const ArbreUpdateSchema = z.object({
  id: z.number().int().positive(),
  diametre: z.number().int().positive("Le diamètre doit être un nombre positif").nullable().optional(),
  age_estime: z.number().int().nullable().optional(),
  coord: CoordSchema.nullable().optional(),
  code_site: z.string().max(255, "Le code site ne peut pas dépasser 255 caractères").nullable().optional(),
  num_sujet: z.string().max(255, "Le numéro de sujet ne peut pas dépasser 255 caractères").nullable().optional(),
  critere: z.any().nullable().optional(), // JSON field
  implantation: z.string().max(255, "L'implantation ne peut pas dépasser 255 caractères").nullable().optional(),
  implantation_other: z.string().max(255, "L'implantation autre ne peut pas dépasser 255 caractères").nullable().optional(),
  domaine: z.string().max(255, "Le domaine ne peut pas dépasser 255 caractères").nullable().optional(),
  nuisance: z.any().nullable().optional(), // JSON field
  proximite: z.any().nullable().optional(), // JSON field
  taux_freq: z.string().max(255, "Le taux de fréquence ne peut pas dépasser 255 caractères").nullable().optional(),
  type_passage: z.any().nullable().optional(), // JSON field
  accessibilite: z.string().max(255, "L'accessibilité ne peut pas dépasser 255 caractères").nullable().optional(),
  abattage: z.string().max(255, "L'abattage ne peut pas dépasser 255 caractères").nullable().optional(),
  travaux_collet: z.string().nullable().optional(),
  travaux_tronc: z.string().nullable().optional(),
  travaux_houppier: z.string().nullable().optional(),
  date_travaux: z.string().max(255, "La date des travaux ne peut pas dépasser 255 caractères").nullable().optional(),
  date_pro_visite: z.string().max(255, "La date de prochaine visite ne peut pas dépasser 255 caractères").nullable().optional(),
  com_pro_visite: z.string().nullable().optional(),
  caract_pied: z.any().nullable().optional(), // JSON field
  caract_tronc: z.string().max(255, "Les caractéristiques du tronc ne peuvent pas dépasser 255 caractères").nullable().optional(),
  hauteur: z.number().int().positive("La hauteur doit être un nombre positif").nullable().optional(),
  port_arbre: z.string().max(255, "Le port de l'arbre ne peut pas dépasser 255 caractères").nullable().optional(),
  stade_dev: z.string().max(255, "Le stade de développement ne peut pas dépasser 255 caractères").nullable().optional(),
  etat_san_collet: z.any().nullable().optional(), // JSON field
  etat_san_tronc: z.any().nullable().optional(), // JSON field
  etat_san_houppier: z.any().nullable().optional(), // JSON field
  address: z.string().max(255, "L'adresse ne peut pas dépasser 255 caractères").nullable().optional(),
  com_access: z.string().nullable().optional(),
  dict: z.any().nullable().optional(), // JSON field
  risque: z.any().nullable().optional(), // JSON field
  proximite_other: z.string().nullable().optional(),
  accessibilite_other: z.string().nullable().optional(),
  caract_pied_other: z.string().nullable().optional(),
  caract_tronc_multiples: z.number().int().nullable().optional(),
  etat_san_collet_cavite: z.number().int().nullable().optional(),
  etat_san_collet_champignons: IdListSchema, // JSON field
  etat_san_tronc_cavite: z.number().int().nullable().optional(),
  etat_san_tronc_corps_etranger: z.string().max(255).nullable().optional(),
  etat_san_tronc_champignons: IdListSchema, // JSON field
  etat_san_tronc_nuisibles: IdListSchema, // JSON field
  etat_san_houppier_champignons: IdListSchema, // JSON field
  etat_san_houppier_nuisibles: IdListSchema,
  risque_general: z.any().nullable().optional(), // JSON field
  travaux_commentaire: z.string().nullable().optional(),
  critere_other: z.string().nullable().optional(),
  risque_general_other: z.string().nullable().optional(),
  type_passage_other: z.string().nullable().optional(),
  type_intervention: z.string().max(255).nullable().optional(),
  etat_san_general: z.any().nullable().optional(), // JSON field
  user_edited_date_travaux: z.string().nullable().optional(),
  user_edited_date_pro_visite: z.string().nullable().optional(),
  travaux_tronc_other: z.string().nullable().optional(),
  travaux_collet_other: z.string().nullable().optional(),
  travaux_houppier_other: z.string().max(2000).nullable().optional(),
  proximite_with_dict: z.any().nullable().optional(), // JSON field
  ville: z.string().max(155).nullable().optional(),
  pays: z.string().max(255).nullable().optional(),
  travaux_tronc_protection: z.string().max(100).nullable().optional(),
  img1: z.string().max(255).nullable().optional(),
  img2: z.string().max(255).nullable().optional(),
  img3: z.string().max(255).nullable().optional(),
  etat_san_tronc_nuisibles_autres: z.string().max(150).nullable().optional(),
  etat_san_houppier_nuisibles_autres: z.string().max(150).nullable().optional(),
  travaux_collet_multiple: z.any().nullable().optional(), // JSON field
  travaux_tronc_multiple: z.any().nullable().optional(), // JSON field
  travaux_houppier_multiple: z.any().nullable().optional(), // JSON field
  status_travaux: z.boolean().default(true).optional(),
  /** Visite réalisée (séparée de status_travaux). */
  status_visite: z.boolean().nullable().optional(),
  etat_san_collet_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_tronc_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_houppier_champignons_autres: z.string().max(255).nullable().optional(),
  etat_san_collet_other: z.string().max(255).nullable().optional(),
  etat_san_tronc_other: z.string().nullable().optional(),
  etat_san_houppier_other: z.string().nullable().optional(),
  etat_san_general_other: z.string().max(200).nullable().optional(),
  espece_id: z.number().int().positive("L'ID de l'espèce doit être un nombre positif"),


});

export const ArbreListResponseSchema = z.object({
  data: z.array(ArbreSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const ArbreResponseSchema = z.object({
  data: ArbreSchema,
  message: z.string().optional(),
});

export const ArbreSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  code_site: z.string().optional(),
  num_sujet: z.string().optional(),
  espece_id: z.number().int().positive().optional(),
  ville: z.string().optional(),
  pays: z.string().optional(),
  status_travaux: z.boolean().optional(),
  status_visite: z.boolean().optional(),
  includeDeleted: z.boolean().default(false),
  limit: z.number().default(10),
  page: z.number().default(1),
  search: z.string().optional(),
});

export const ArbreMinimalDataSchema = ArbreSchema.pick({
  created_at: true,
  updated_at: true,
  espece: true,
  code_site: true,
  num_sujet: true,
  address: true,
  diametre: true,
  hauteur: true,
  coord: true,
});

export type Arbre = z.infer<typeof ArbreSchema>;
export type ArbreCreateInput = z.infer<typeof ArbreCreateSchema>;
export type ArbreUpdateInput = z.infer<typeof ArbreUpdateSchema>;
export type ArbreSelectInput = z.infer<typeof ArbreSelectSchema>;
export type ArbreMinimalData = z.infer<typeof ArbreMinimalDataSchema>;
// Form-specific types for frontend
export type ArbreFormData = Omit<ArbreCreateInput, 'status_travaux'> & {
  status_travaux?: boolean;
};

export type ArbreEditFormData = Omit<ArbreUpdateInput, 'id'>;

// Utility types for different UI states
export type ArbreDisplayItem = Pick<Arbre, 'id' | 'code_site' | 'num_sujet' | 'address' | 'ville' | 'status_travaux'>;
export type ArbreSelectOption = Pick<Arbre, 'id' | 'code_site' | 'num_sujet'>;
export type ArbreTableItem = Pick<Arbre, 'id' | 'code_site' | 'num_sujet' | 'address' | 'ville' | 'pays' | 'status_travaux' | 'created_at' | 'updated_at'>;

export type ArbreResponse = z.infer<typeof ArbreResponseSchema>;
export type ArbreListResponse = z.infer<typeof ArbreListResponseSchema>;

// Coordinate update schema for updating only lat/lng
export const ArbreCoordinateUpdateSchema = z.object({
  lat: z.number().min(-90, "La latitude doit être entre -90 et 90").max(90, "La latitude doit être entre -90 et 90"),
  lng: z.number().min(-180, "La longitude doit être entre -180 et 180").max(180, "La longitude doit être entre -180 et 180")
});

export type ArbreCoordinateUpdateInput = z.infer<typeof ArbreCoordinateUpdateSchema>;

// Address update schema for updating address fields and coordinates
export const ArbreAddressUpdateSchema = z.object({
  lat: z.number().min(-90, "La latitude doit être entre -90 et 90").max(90, "La latitude doit être entre -90 et 90"),
  lng: z.number().min(-180, "La longitude doit être entre -180 et 180").max(180, "La longitude doit être entre -180 et 180"),
  address: z.string().max(255, "L'adresse ne peut pas dépasser 255 caractères").nullable().optional(),
  ville: z.string().max(155, "La ville ne peut pas dépasser 155 caractères").nullable().optional(),
  zip_code: z.string().max(100, "Le code postal ne peut pas dépasser 100 caractères").nullable().optional(),
});

export type ArbreAddressUpdateInput = z.infer<typeof ArbreAddressUpdateSchema>;

export const validateArbreCreate = (data: unknown) => {
  return ArbreCreateSchema.safeParse(data);
};

export const validateArbreUpdate = (data: unknown) => {
  return ArbreUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validateArbreSelect = (data: unknown) => {
  return ArbreSelectSchema.safeParse(data);
};

export const validateArbreCoordinateUpdate = (data: unknown) => {
  return ArbreCoordinateUpdateSchema.safeParse(data);
};

export const validateArbreAddressUpdate = (data: unknown) => {
  return ArbreAddressUpdateSchema.safeParse(data);
};